import type { LibraryItemResponse } from "@poseref/shared";
import { Box, FileBox, Folder, Image, Layers } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  COLOR_ACCENT,
  COLOR_ACTIVE_BG_SUBTLE,
  COLOR_BORDER,
  COLOR_CAUTION,
  COLOR_MOD_SUBSURF,
  COLOR_SURFACE,
  COLOR_TEXT,
  COLOR_TEXT_DIM,
  COLOR_TEXT_FAINT,
  COLOR_TYPE_BLEND,
  COLOR_TYPE_POSE,
  FONT_SIZE_BODY,
  FONT_SIZE_TINY,
  RADIUS_CONTROL,
  RADIUS_SM,
  SPACE_MD,
  SPACE_SM,
} from "../tokens.ts";

const CARD_ASPECT = 1; // Square thumbnails

/** Type badge colors */
const TYPE_COLORS: Record<string, string> = {
  scene: COLOR_ACCENT,
  object: COLOR_MOD_SUBSURF,
  collection: COLOR_CAUTION,
  blend: COLOR_TYPE_BLEND,
  pose: COLOR_TYPE_POSE,
  reference_image: "#8B9DC3",
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const days = Math.floor(diff / 86400000);

  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatBytesCompact(bytes: number): string {
  if (bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} K`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} M`;
}

interface LibraryCardProps {
  item: LibraryItemResponse;
  selected: boolean;
  renaming?: boolean;
  folderName?: string;
  onSelect: (e: React.MouseEvent) => void;
  onOpen: () => void;
  onRename: (name: string) => void;
  onRenameEnd?: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onDragStart: (e: React.DragEvent) => void;
}

export function LibraryCard({
  item,
  selected,
  renaming,
  folderName,
  onSelect,
  onOpen,
  onRename,
  onRenameEnd,
  onContextMenu,
  onDragStart,
}: LibraryCardProps) {
  const [isRenaming, setIsRenaming] = useState(false);
  const [editValue, setEditValue] = useState(item.name);
  const inputRef = useRef<HTMLInputElement>(null);

  // Enter rename mode when triggered externally (context menu)
  useEffect(() => {
    if (renaming) {
      setEditValue(item.name);
      setIsRenaming(true);
    }
  }, [renaming, item.name]);

  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  const commitRename = useCallback(() => {
    setIsRenaming(false);
    onRenameEnd?.();
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== item.name) {
      onRename(trimmed);
    } else {
      setEditValue(item.name);
    }
  }, [editValue, item.name, onRename, onRenameEnd]);

  const typeColor = TYPE_COLORS[item.type] ?? COLOR_TEXT_DIM;

  return (
    <div
      onClick={onSelect}
      onDoubleClick={onOpen}
      onContextMenu={(e) => {
        e.preventDefault();
        onContextMenu(e);
      }}
      draggable
      onDragStart={onDragStart}
      style={{
        background: selected ? COLOR_ACTIVE_BG_SUBTLE : COLOR_SURFACE,
        border: `1.5px solid ${selected ? COLOR_ACCENT : COLOR_BORDER}`,
        borderRadius: RADIUS_CONTROL,
        cursor: "pointer",
        overflow: "hidden",
        transition: "border-color 120ms, background 120ms",
        userSelect: "none",
      }}
    >
      {/* Thumbnail */}
      <div
        style={{
          width: "100%",
          aspectRatio: `${CARD_ASPECT}`,
          background: "rgba(0,0,0,0.3)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {item.thumbnailUrl ? (
          <img
            src={item.thumbnailUrl}
            alt={item.name}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
            draggable={false}
          />
        ) : (
          <PlaceholderIcon type={item.type} />
        )}
        {folderName && (
          <span
            style={{
              position: "absolute",
              top: SPACE_SM,
              left: SPACE_SM,
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
              padding: "1px 5px",
              borderRadius: RADIUS_SM,
              background: "rgba(0,0,0,0.6)",
              color: COLOR_TEXT_FAINT,
              fontSize: FONT_SIZE_TINY,
              fontWeight: 500,
              lineHeight: "16px",
              maxWidth: "calc(100% - 60px)",
            }}
          >
            <Folder size={10} style={{ flexShrink: 0 }} />
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {folderName}
            </span>
          </span>
        )}
        {item.fileSize > 0 && (
          <span
            style={{
              position: "absolute",
              top: SPACE_SM,
              right: SPACE_SM,
              padding: "1px 5px",
              borderRadius: RADIUS_SM,
              background: "rgba(0,0,0,0.6)",
              color: COLOR_TEXT_FAINT,
              fontSize: FONT_SIZE_TINY,
              fontWeight: 500,
              lineHeight: "16px",
            }}
          >
            {formatBytesCompact(item.fileSize)}
          </span>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: `${SPACE_MD}px ${SPACE_MD}px ${SPACE_SM}px` }}>
        {/* Name (or rename input) */}
        {isRenaming ? (
          <input
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitRename();
              if (e.key === "Escape") {
                setIsRenaming(false);
                onRenameEnd?.();
                setEditValue(item.name);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              padding: "2px 4px",
              background: COLOR_SURFACE,
              border: `1px solid ${COLOR_ACCENT}`,
              borderRadius: RADIUS_SM,
              color: COLOR_TEXT,
              fontSize: FONT_SIZE_BODY,
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        ) : (
          <div
            onDoubleClick={(e) => {
              e.stopPropagation();
              setIsRenaming(true);
            }}
            style={{
              fontSize: FONT_SIZE_BODY,
              color: COLOR_TEXT,
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.name}
          </div>
        )}

        {/* Date + type badge */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: 4,
            gap: 4,
          }}
        >
          <span style={{ fontSize: FONT_SIZE_TINY, color: COLOR_TEXT_FAINT }}>
            {formatDate(item.updatedAt)}
          </span>
          <span
            style={{
              fontSize: FONT_SIZE_TINY,
              color: typeColor,
              textTransform: "capitalize",
              fontWeight: 500,
              flexShrink: 0,
            }}
          >
            {item.type}
          </span>
        </div>
      </div>
    </div>
  );
}

function PlaceholderIcon({ type }: { type: string }) {
  const color = TYPE_COLORS[type] ?? COLOR_TEXT_FAINT;
  const size = 32;
  if (type === "object")
    return <Box size={size} style={{ color, opacity: 0.6 }} />;
  if (type === "collection")
    return <Layers size={size} style={{ color, opacity: 0.6 }} />;
  if (type === "blend")
    return <FileBox size={size} style={{ color, opacity: 0.6 }} />;
  return <Image size={size} style={{ color, opacity: 0.6 }} />;
}
